import React, { useEffect, useState } from 'react';
import { T } from 'src/shared/helpers';
import {
    PropertiesPanelLeafConfig,
    PropertiesPanelMapThumb
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { pickFiles } from './pickFiles';
import { fileNameOf } from './sceneUrls';

// LO SLOT DI UNA TEXTURE — la riga `resource` di sempre, con dentro una mappa.
//
// Il pannello vecchio disegna una mappa come «Upload · cestino · miniatura»: un campo file con
// accanto ciò che ha caricato. Qui la mappa è un POSTO, come l'ambiente del World e il materiale di
// un oggetto: miniatura, nome, misura, e i due gesti in punta — sostituisci e svuota.
//
// SI RISCRIVE LA COPIA, NON LA SORGENTE. `createTexturePropertyConfig` produce la riga `image` di
// sempre, e la conversione avviene solo dove si compone il pannello della scena.
//
// COSA NON C'È, E PERCHÉ. Nel mockup lo slot ha anche la freccia che porta alla texture nella sua
// libreria, e sostituire apre l'elenco delle texture: qui una libreria di texture non esiste
// (docs/scene-editor-new-sections.md § 1), quindi non c'è un posto dove portare né un elenco da
// aprire. Sostituire vuol dire caricare un file.

type ImageLeaf = Extract<PropertiesPanelLeafConfig, { type: 'image' }>;

// LA MISURA SI LEGGE DALL'IMMAGINE, quella che la miniatura mostra: il motore la saprebbe solo per
// la mappa desktop, mentre la versione mobile vive come indirizzo nei metadati. Si ricorda per
// indirizzo, perché il pannello si ridisegna spesso e un file non cambia misura.
const sizes = new Map<string, string | null>();

const TextureSize: React.FC<{ src: string; fallback: string | null }> = ({ src, fallback }) => {
    const [size, setSize] = useState(sizes.get(src));

    useEffect(() => {
        if (sizes.has(src)) {
            setSize(sizes.get(src));
            return;
        }

        let alive = true;
        const settle = (value: string | null) => {
            sizes.set(src, value);
            if (alive) setSize(value);
        };
        const image = new Image();
        image.onload = () => settle(`${image.naturalWidth} × ${image.naturalHeight}`);
        // Un .hdr o un .env il browser non li sa leggere: la misura non c'è, e resta il formato.
        image.onerror = () => settle(null);
        image.src = src;

        return () => {
            alive = false;
        };
    }, [src]);

    return <>{size ?? fallback}</>;
};

// UN FILE APPENA SCELTO NON HA ANCORA UN NOME: `blob:` e `data:` sono la forma di una texture
// caricata e non ancora salvata, e il loro ultimo pezzo è un identificativo. Finché è così lo slot
// dice che cos'è — una texture — invece di mostrare una sigla.
const readableName = (url: string) => {
    if (/^(?:data:|blob:)/i.test(url)) return null;
    const name = fileNameOf(url);
    try {
        return name && decodeURIComponent(name);
    } catch {
        return name;
    }
};

const formatOf = (url: string) => {
    const name = readableName(url);
    const dot = name ? name.lastIndexOf('.') : -1;
    return name && dot > 0 ? name.substring(dot + 1).toUpperCase() : null;
};

/**
 * Il campo file di una mappa, riscritto come slot. `mobile` è la seconda riga dello stesso canale:
 * si chiama col proprio RUOLO e non col nome del file, perché è la variante di quella sopra.
 */
export const textureSlot = (image: ImageLeaf, mobile: boolean): PropertiesPanelLeafConfig => {
    const src = image.images?.[0]?.src || undefined;
    const upload = () => pickFiles(image.accept ?? 'image/*', (files) => image.onUpload?.(files));

    return {
        type: 'resource',
        id: image.id,
        preview: <PropertiesPanelMapThumb $src={src} />,
        name: src ? (mobile ? T._('Mobile', 'Admin') : (readableName(src) ?? T._('Texture', 'Admin'))) : undefined,
        caption: src ? <TextureSize src={src} fallback={formatOf(src)} /> : undefined,
        emptyLabel: mobile ? T._('Mobile', 'Admin') : T._('Empty slot', 'Admin'),
        emptyCaption: mobile ? T._('Upload a mobile version', 'Admin') : T._('Upload a texture', 'Admin'),
        onPick: upload,
        onReplace: upload,
        // IL CESTINO C'È ANCHE SULLA VERSIONE MOBILE (utente, 2026-09-15). Nel mockup no — là la
        // variante esiste finché esiste la mappa — ma toglierla è una cosa che questo backoffice sa
        // già fare, e un gesto che c'era non si perde per strada.
        onClear: src ? image.onDelete : undefined,
        disabled: image.disabled
    };
};
